<template>
    <div class="sass-captcha">
      <div class="captcha-header">
        <h3>安全验证</h3>
        <p>请滑动下方滑块完成验证</p>
      </div>
      <div class="slider-container">
        <div class="slider-wrapper">
          <div 
            class="slider-track"
            ref="trackRef"
            @mousedown.prevent="startDrag"
            @touchstart.prevent="startDrag"
          >
            <div class="slider-bg" :style="{ width: `${position}px` }"></div>
            <div class="slider-hint">
              {{ isVerified ? successText : hintText }}
            </div>
            <div 
              class="slider-thumb"
              :style="{ left: `${position}px` }"
              :class="{ 'slider-thumb-success': isVerified }"
            >
              <svg viewBox="0 0 24 24" class="thumb-icon">
                <path v-if="!isVerified" d="M7 10l5 5 5-5z"/>
                <path v-else d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
              </svg>
            </div>
          </div>
        </div>
        <div class="slider-status">
          <span v-if="isVerified" class="status-success">
            <svg viewBox="0 0 24 24" class="status-icon">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
            验证通过
          </span>
          <button v-else class="refresh-btn" @click.prevent="reset">
            <svg viewBox="0 0 24 24" class="refresh-icon">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
            </svg>
            刷新验证
          </button>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted, onUnmounted } from 'vue'
  
  const props = defineProps({
    hintText: {
      type: String,
      default: '向右滑动完成验证'
    },
    successText: {
      type: String,
      default: '验证成功'
    },
    themeColor: {
      type: String,
      default: '#409EFF'  // SaaS 常用蓝色主题
    }
  })
  
  const emit = defineEmits(['success', 'fail', 'change'])
  
  const trackRef = ref(null)
  const position = ref(0)
  const isDragging = ref(false)
  const startX = ref(0)
  const startPosition = ref(0)
  const isVerified = ref(false)
  const trackWidth = ref(260)
  
  const updateDimensions = () => {
    if (trackRef.value) {
      trackWidth.value = trackRef.value.offsetWidth
    }
  }
  
  const startDrag = (e) => {
    if (isVerified.value) return
    
    isDragging.value = true
    startX.value = e.clientX || e.touches[0].clientX
    startPosition.value = position.value
    
    window.addEventListener('mousemove', handleDrag)
    window.addEventListener('mouseup', stopDrag)
    window.addEventListener('touchmove', handleDrag)
    window.addEventListener('touchend', stopDrag)
  }
  
  const handleDrag = (e) => {
    if (!isDragging.value) return
    
    const clientX = e.clientX || e.touches[0].clientX
    const diffX = clientX - startX.value
    let newPosition = startPosition.value + diffX
    
    newPosition = Math.max(0, Math.min(newPosition, trackWidth.value))
    position.value = newPosition
    
    const threshold = trackWidth.value * 0.9
    if (newPosition >= threshold && !isVerified.value) {
      isVerified.value = true
      position.value = trackWidth.value
      emit('success')
    }
    
    emit('change', newPosition / trackWidth.value)
  }
  
  const stopDrag = () => {
    if (!isDragging.value) return
    
    isDragging.value = false
    
    if (!isVerified.value) {
      position.value = 0
      emit('fail')
    }
    
    window.removeEventListener('mousemove', handleDrag)
    window.removeEventListener('mouseup', stopDrag)
    window.removeEventListener('touchmove', handleDrag)
    window.removeEventListener('touchend', stopDrag)
  }
  
  const reset = () => {
    isVerified.value = false
    position.value = 0
    emit('change', 0)
  }
  
  onMounted(() => {
    updateDimensions()
    window.addEventListener('resize', updateDimensions)
  })
  
  onUnmounted(() => {
    window.removeEventListener('resize', updateDimensions)
  })
  
  defineExpose({
    reset
  })
  </script>
  
  <style scoped>
  .sass-captcha {
    width: 410px;
    padding: 24px;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    border: 1px solid #ebeef5;
  }
  
  .captcha-header {
    text-align: center;
    margin-bottom: 20px;
  }
  
  .captcha-header h3 {
    margin: 0 0 8px 0;
    font-size: 16px;
    color: #303133;
    font-weight: 500;
  }
  
  .captcha-header p {
    margin: 0;
    font-size: 14px;
    color: #909399;
  }
  
  .slider-container {
    margin-top: 16px;
  }
  
  .slider-wrapper {
    width: 100%;
  }
  
  .slider-track {
    position: relative;
    width: 100%;
    height: 40px;
    background-color: #f5f7fa;
    border-radius: 20px;
    cursor: pointer;
    user-select: none;
    touch-action: none;
    overflow: hidden;
    border: 1px solid #dcdfe6;
    transition: border-color 0.2s;
  }
  
  .slider-track:hover {
    border-color: #c0c4cc;
  }
  
  .slider-bg {
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    background-color: v-bind('props.themeColor');
    opacity: 0.8;
    transition: width 0.2s ease;
  }
  
  .slider-hint {
    position: absolute;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #606266;
    font-size: 14px;
    font-weight: 400;
    pointer-events: none;
    transition: color 0.3s;
  }
  
  .slider-thumb {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 40px;
    height: 40px;
    background-color: #fff;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    cursor: grab;
    transition: left 0.1s ease, background-color 0.3s;
  }
  
  .slider-thumb:active {
    cursor: grabbing;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
  }
  
  .slider-thumb-success {
    background-color: v-bind('props.themeColor');
  }
  
  .thumb-icon {
    width: 18px;
    height: 18px;
    fill: #909399;
    transition: fill 0.3s;
  }
  
  .slider-thumb-success .thumb-icon {
    fill: white;
  }
  
  .slider-status {
    margin-top: 16px;
    text-align: center;
    height: 24px;
  }
  
  .status-success {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: v-bind('props.themeColor');
    font-size: 14px;
    font-weight: 500;
  }
  
  .status-icon {
    width: 16px;
    height: 16px;
    margin-right: 6px;
    fill: currentColor;
  }
  
  .refresh-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    color: #606266;
    font-size: 13px;
    cursor: pointer;
    padding: 4px;
    transition: color 0.2s;
  }
  
  .refresh-btn:hover {
    color: v-bind('props.themeColor');
  }
  
  .refresh-icon {
    width: 14px;
    height: 14px;
    margin-right: 4px;
    fill: currentColor;
  }
  
  @media (max-width: 480px) {
    .sass-captcha {
      width: 280px;
      padding: 20px;
    }
    
    .slider-track {
      height: 36px;
    }
    
    .slider-thumb {
      width: 36px;
      height: 36px;
    }
    
    .thumb-icon {
      width: 16px;
      height: 16px;
    }
  }
  </style>